import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@tabipla/ui";
import {
  Users,
  AlertCircle,
  CheckCircle2,
  MapPin,
  Shield,
  Calendar,
  Smartphone,
} from "lucide-react";
import {
  useInvitationInfo,
  useJoinAsGuest,
  useHandlePendingInvitation,
} from "../../application/hooks/useInvitations";
import { useAuth } from "../../application/hooks/useAuth";
import { LoadingState } from "../components/LoadingState";

export function JoinGroup() {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  const { data: invitationInfo, isLoading, error } = useInvitationInfo(code);
  const joinMutation = useJoinAsGuest(code ?? "");
  const { isLoading: isAuthLoading } = useAuth();
  const { setPendingInvitation } = useHandlePendingInvitation();

  const [name, setName] = useState("");
  const [isJoined, setIsJoined] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      const result = await joinMutation.mutateAsync({ name: name.trim() });
      if (!result) return;

      // Token storage is handled by the useJoinAsGuest hook

      setIsJoined(true);

      // Redirect to the group page after a short delay
      // Use window.location.href instead of navigate() to force a full page reload
      // This ensures Better Auth picks up the new session cookie
      setTimeout(() => {
        window.location.href = `/groups/${result.group.id}`;
      }, 2000);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleLoginAndJoin = () => {
    // Store the invitation code to join after login
    setPendingInvitation(code ?? "");
    navigate("/login");
  };

  if (isLoading || isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4">
        <LoadingState message={t("invitations.loading")} />
      </div>
    );
  }

  if (error || !invitationInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4">
        <div className="animate-scale-in w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <h2 className="mb-2 text-xl font-semibold">{t("invitations.notFound")}</h2>
                <p className="text-muted-foreground">{t("invitations.notFoundDescription")}</p>
                <Button className="mt-6" size="lg" onClick={() => navigate("/")}>
                  {t("common.backToHome")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!invitationInfo.isValid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4">
        <div className="animate-scale-in w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                  <AlertCircle className="h-8 w-8 text-yellow-500" />
                </div>
                <h2 className="mb-2 text-xl font-semibold">{t("invitations.expired")}</h2>
                <p className="text-muted-foreground">{t("invitations.expiredDescription")}</p>
                <Button className="mt-6" size="lg" onClick={() => navigate("/")}>
                  {t("common.backToHome")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isJoined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4">
        <div className="animate-scale-in w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <h2 className="mb-2 text-xl font-semibold">{t("invitations.joinedSuccess")}</h2>
                <p className="text-muted-foreground">
                  {t("invitations.redirecting", { groupName: invitationInfo.groupName })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Format date range for trip
  const formatDateRange = () => {
    if (!invitationInfo.trip?.startDate && !invitationInfo.trip?.endDate) {
      return t("trips.dateUndecided");
    }
    const start = invitationInfo.trip.startDate
      ? new Date(invitationInfo.trip.startDate).toLocaleDateString("ja-JP", {
          month: "short",
          day: "numeric",
        })
      : "";
    const end = invitationInfo.trip.endDate
      ? new Date(invitationInfo.trip.endDate).toLocaleDateString("ja-JP", {
          month: "short",
          day: "numeric",
        })
      : "";
    return start && end ? `${start} - ${end}` : start || end || t("trips.dateUndecided");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4">
      <div className="animate-scale-in w-full max-w-md">
        {/* Logo */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary shadow-lg">
            <MapPin className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="text-xl font-bold">Tabitto</h1>
        </div>

        <Card className="border-0 shadow-xl">
          {/* Rich Preview Header */}
          <CardHeader className="pb-4">
            {/* Inviter Info */}
            <div className="mb-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Avatar className="h-6 w-6">
                <AvatarImage src={invitationInfo.invitedBy.image ?? undefined} />
                <AvatarFallback className="text-xs">
                  {invitationInfo.invitedBy.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span>
                <strong className="text-foreground">{invitationInfo.invitedBy.name}</strong>
                {t("invitations.invitedYou")}
              </span>
            </div>

            {/* Trip Info (if available) */}
            {invitationInfo.trip && (
              <div className="mb-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-2xl shadow-sm">
                    {invitationInfo.trip.coverEmoji || "✈️"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold text-foreground">
                      {invitationInfo.trip.title}
                    </h3>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{invitationInfo.trip.destination}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDateRange()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Group Info */}
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-lg">
                {t("invitations.joinTitle", { groupName: invitationInfo.groupName })}
              </CardTitle>
              <CardDescription className="mt-1">{t("invitations.joinDescription")}</CardDescription>
            </div>

            {/* Member Preview */}
            {invitationInfo.members.length > 0 && (
              <div className="mt-4 flex items-center justify-center">
                <div className="flex -space-x-2">
                  {invitationInfo.members.slice(0, 4).map((member) => (
                    <Avatar key={member.id} className="h-8 w-8 border-2 border-background">
                      <AvatarImage src={member.image ?? undefined} />
                      <AvatarFallback className="bg-primary/10 text-xs">
                        {member.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {invitationInfo.memberCount > 4 && (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium">
                      +{invitationInfo.memberCount - 4}
                    </div>
                  )}
                </div>
                <span className="ml-3 text-sm text-muted-foreground">
                  {t("invitations.memberCount", { count: invitationInfo.memberCount })}
                </span>
              </div>
            )}
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("invitations.yourName")}</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("invitations.namePlaceholder")}
                  required
                  disabled={joinMutation.isPending}
                />
              </div>

              {joinMutation.isError && (
                <div className="flex items-center gap-2 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {t("invitations.joinError")}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={joinMutation.isPending || !name.trim()}
              >
                {joinMutation.isPending ? t("invitations.joining") : t("invitations.join")}
              </Button>
            </form>

            <div className="mt-6 border-t pt-6">
              <p className="mb-4 text-center text-sm text-muted-foreground">
                {t("invitations.haveAccount")}
              </p>
              <Button variant="outline" className="w-full" size="lg" onClick={handleLoginAndJoin}>
                {t("invitations.loginAndJoin")}
              </Button>
            </div>

            {/* Security & Features Badge */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3 text-green-500" />
                <span>{t("invitations.secure")}</span>
              </div>
              <div className="flex items-center gap-1">
                <Smartphone className="h-3 w-3" />
                <span>{t("invitations.noInstall")}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
